import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, Typography, Radio, RadioGroup, FormControlLabel } from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import DialogTemplate from 'components/shared/DialogTemplate';
import { Select, Input, Checkbox } from 'components/FormInputs';
import { validateDacCompliance, normalizeData } from 'logic/cottage';
import { useFormik } from 'components/shared/Formik';

const DacComplianceAddress = ({ isLoading, onSubmit, onClose, cottage }) => {
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const intl = useIntl();
  const formik = useFormik({
    initialValues: {
      address_street: '',
      address_postal_code: '',
      address_city: '',
      property_designation_type: '',
      rent_out_type: '',
      property_designation: '',
      cottage_village: false,
      ...cottage,
    },
    enableReinitialize: true,
    validate: validateDacCompliance({ intl, dialog: 'position' }),
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: async (values) => {
      await onSubmit(normalizeData(values));
    },
  });

  const {
    values,
    errors,
    handleSubmit,
    handleChange,
    isSubmitting,
    dirty,
    submitForm,
    handleBlur,
  } = formik;

  const getPropertyDesignationLabel = () => {
    if (values.cottage_village) {
      return (
        <FormattedMessage
          id="Property designation.cottage_village"
          defaultMessage="Cottage number"
        />
      );
    }
    if (values.property_designation_type === 'APARTMENT') {
      return (
        <FormattedMessage id="Property designation.apartment" defaultMessage="Apartment number" />
      );
    }
    return <FormattedMessage id="Property designation" defaultMessage="Property designation" />;
  };
  const getPropertyDesignationHelp = () => {
    if (values.cottage_village) {
      return (
        <FormattedMessage
          id="property_designation_help.cottage_village"
          defaultMessage="Cottage number (max 5 characters)"
        />
      );
    }
    if (values.property_designation_type === 'APARTMENT') {
      return (
        <FormattedMessage
          id="property_designation_help.apartment"
          defaultMessage="Apartment number (4 digits)"
        />
      );
    }
    return (
      <FormattedMessage
        id="property_designation_help"
        defaultMessage="Property designation (Example: Kommun Traktnamn 10:10)"
      />
    );
  };

  return (
    <DialogTemplate
      title={
        <>
          <FormattedMessage id="Address" defaultMessage="Address" />
          &nbsp;
          <>
            #{cottage?.id}
            {!isSmall && <>{cottage?.title_sv ? <> - "{cottage?.title_sv}"</> : ''}</>}
          </>
        </>
      }
      isUpdating={isLoading || isSubmitting}
      pristine={!dirty}
      onClose={onClose}
      onSave={submitForm}
    >
      <Box pb={4}>
        <form onSubmit={handleSubmit} noValidate>
          <Box mb={2}>
            <Typography>
              <FormattedMessage
                id="cottages.addressDacCompliance"
                defaultMessage="Check that all address information is entered and correct for the accommodation"
              />
            </Typography>
            <Box mt={2}>
              <Typography variant="body2">
                <FormattedMessage id="Type of rental" defaultMessage="Type of rental" />
              </Typography>
              <Box mb={1}>
                <Select
                  name="property_designation_type"
                  value={values.property_designation_type}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errors={errors}
                  label={<FormattedMessage id="Property type" defaultMessage="Property type" />}
                  options={[
                    {
                      value: 'HOUSE',
                      label: intl.formatMessage({
                        id: 'property_designation_type.house',
                        defaultMessage: 'House',
                      }),
                    },
                    {
                      value: 'COTTAGE',
                      label: intl.formatMessage({
                        id: 'property_designation_type.cottage',
                        defaultMessage: 'Cottage',
                      }),
                    },
                    {
                      value: 'APARTMENT',
                      label: intl.formatMessage({
                        id: 'property_designation_type.apartment',
                        defaultMessage: 'Apartment',
                      }),
                    },
                  ]}
                  required
                />
              </Box>
              <Box>
                <RadioGroup
                  row
                  name="rent_out_type"
                  value={values.rent_out_type}
                  defaultValue="ENTIRE_HOME"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errors={errors}
                  required
                >
                  <FormControlLabel
                    value="ENTIRE_HOME"
                    control={<Radio color="primary" />}
                    label={intl.formatMessage({
                      id: 'rent_out_type.entire_home',
                      defaultMessage: 'Entire home',
                    })}
                  />
                  <FormControlLabel
                    value="PART_OF_HOME"
                    control={<Radio color="primary" />}
                    label={intl.formatMessage({
                      id: 'rent_out_type.part_of_home',
                      defaultMessage: 'Part of home',
                    })}
                  />
                </RadioGroup>
              </Box>
            </Box>
            <Input
              name="address_street"
              value={values.address_street}
              onChange={handleChange}
              onBlur={handleBlur}
              errors={errors}
              label={<FormattedMessage id="Address" defaultMessage="Address" />}
              InputProps={{ inputProps: { maxLength: 255 } }}
              required
            />
            <Box display="flex">
              <Box flex={1} mr={2}>
                <Input
                  name="address_postal_code"
                  value={values.address_postal_code}
                  onChange={(e) => {
                    e.target.value = (e.target.value || '').replace(/[^0-9]/g, '');
                    return handleChange(e);
                  }}
                  onBlur={handleBlur}
                  errors={errors}
                  label={<FormattedMessage id="Postal Code" defaultMessage="Postal code" />}
                  InputProps={{ inputProps: { maxLength: 50 } }}
                  style={{ minWidth: 110 }}
                  required
                />
              </Box>
              <Box flex={2}>
                <Input
                  name="address_city"
                  value={values.address_city}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errors={errors}
                  label={<FormattedMessage id="CityAlt" defaultMessage="City" />}
                  InputProps={{ inputProps: { maxLength: 100 } }}
                  required
                />
              </Box>
            </Box>
            <Checkbox
              label={
                <FormattedMessage id="cottage_village" defaultMessage="Part of cottage village" />
              }
              name="cottage_village"
              onChange={handleChange}
              value={values.cottage_village}
            />
            <Input
              name="property_designation"
              value={values.property_designation}
              onChange={handleChange}
              onBlur={handleBlur}
              errors={errors}
              label={getPropertyDesignationLabel()}
              InputProps={{ inputProps: { maxLength: 255 } }}
              help={getPropertyDesignationHelp()}
              required
            />
            {values.property_designation_type !== 'APARTMENT' && !values.cottage_village && (
              <>
                <FormattedMessage
                  id="lantmateriet.linktext"
                  defaultMessage="Find your property designation at "
                />
                <a
                  id="lantmateriet-link"
                  href={intl.formatMessage({
                    id: 'lantmateriet.link',
                    defaultMessage: 'https://minkarta.lantmateriet.se/',
                  })}
                  target="_blank"
                  rel="noreferrer"
                >
                  <FormattedMessage id="lantmateriet.text" defaultMessage="Lantmäteriet" />
                </a>
              </>
            )}
          </Box>
        </form>
      </Box>
    </DialogTemplate>
  );
};

DacComplianceAddress.propTypes = {
  isLoading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  cottage: PropTypes.shape({
    id: PropTypes.number,
    title_sv: PropTypes.string,
    address_street: PropTypes.string,
    address_postal_code: PropTypes.string,
    address_city: PropTypes.string,
    property_designation_type: PropTypes.string,
    rent_out_type: PropTypes.string,
    property_designation: PropTypes.string,
    cottage_village: PropTypes.bool,
  }),
};

export default DacComplianceAddress;
